import { ManualAction } from "@api-commons/cta/EvaluatedAction.js";
import { SdkGuiControllerInterface } from "@api-commons/gui/GuiControllerInterface.js";
import { DetectedWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { CollectionTarget } from "@ciaopeople/cp-shatsu/DomHelper";
import { Action } from "@ciaopeople/cp-shatsu/EventHandler";
import { MergedObservable } from "@ciaopeople/cp-shatsu/Observable";
import { UserActionError, UserActionIdentifier } from "../api/commondata/UserActionError.js";
import { EmailLoginAskResponse, RESTUserServices } from "../api/RESTUserServices.js";
import { PropertyConfig } from "../config/PropertyConfig.js";
import { LoginManagerPublic } from "../login/LoginManager.js";
import { MembershipController } from "../membership/MembershipController.js";
import { CpNativeAppIntegration } from "../nativeapps/CpNativeAppIntegration.js";
import { StorableSession } from "../session/StorableSession.js";
import { PushServiceInterface } from "../subscriptions/PushServiceInterface.js";
import { DeviceIdProvider } from "../utils/DeviceIdProvider.js";
import { PersonalDataStorageInterface } from "../utils/PersonalDataStorageInterface.js";
import { ApplicationUrlHandler } from "./modules/ApplicationUrlHandler.js";
import { BookmarkParseAndAttachModule } from "./modules/BookmarkParseAndAttachModule.js";
import { FollowParseAndAttachModule } from "./modules/FollowParseAndAttachModule.js";
import { NewsletterParseAndAttachModule } from "./modules/NewsletterParseAndAttachModule.js";
import { ActionableEvents } from "./modules/ParseAndAttachModule.js";
import { SubscriptionCallbackData } from "./modules/SdkHandledHTMLElement.js";
import { SliderParseAndAttachModule } from "./modules/SliderParseAndAttachModule.js";
import { CTAStatsDetails } from "@api-commons/stats/CTATypes.js";
import { BarrierDisplayMode, LoginExtent } from "./aaa/AAABarrierWithSource.js";
import { SessionObject } from "../session/SessionObject.js";
import { Ask1stPartyEmailLoginOperation } from "@api-commons/api/APIClientInterface.js";
import { isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { Ask1stPartyEmailAuthData } from "@api-commons/api/types/UserDataTypes.js";

export type Modules = {
    sliders?: SliderParseAndAttachModule,
    follows?: FollowParseAndAttachModule,
    newsletters?: NewsletterParseAndAttachModule,
    bookmark?: BookmarkParseAndAttachModule
};

export type ConfirmResolver = (b: boolean) => void;




export type ParseOptions = {
    enable: boolean,
    target: CollectionTarget,
    event?: ActionableEvents,
    confirmCallback?: (data: any, resolver: ConfirmResolver) => void,
};



export type ParseOptionGroup = {
    [k in keyof Modules]: ParseOptions
};

export type OldFormatParseOptions = {
    follows?: boolean,
    sliders?: boolean,
    articlesave?: boolean,
    newsletters?: boolean,
    unfollowConfirm: (data: any, resolver: ConfirmResolver) => void
};


export interface OTPLoginInputData {
    email: string,
    firstName?: string,
    lastName?: string,
    redirect_uri?: string
}


export function isOTPLoginInputData(obj: any): obj is OTPLoginInputData {
    return isObject(obj) &&
           ("email" in obj && isString(obj.email)) &&
           (!("firstName" in obj) || isString(obj.firstName) || !obj.firstName) &&
           (!("lastName" in obj) || isString(obj.lastName) || !obj.lastName);
}


export interface OTPVerificationResponse {
    updated: boolean
}

export function isOTPVerificationResponse(obj: any): obj is OTPVerificationResponse {
    return isObject(obj) &&
           ("updated" in obj && typeof obj.updated === 'boolean');
}

export interface OtpControls {
    generateOtpChallenge(loginData: OTPLoginInputData, operation: Ask1stPartyEmailLoginOperation): Promise<EmailLoginAskResponse>;

    confirmOtp(challenge: EmailLoginAskResponse, code: string): Promise<SessionObject | OTPVerificationResponse>;
}
export interface ClientSdkCorePublic_constructor {
    new(): ClientSdkCorePublic;
}
export interface ClientSdkCorePublic 
    extends CpNativeAppIntegration, 
            LoginManagerPublic, 
            RESTUserServices, 
            PersonalDataStorageInterface,
            DeviceIdProvider,
            MembershipController,
            OtpControls
{

    getCurrentSession(): StorableSession
    getCurrentSessionReact(): void 

    genderSim(gender: string): void 
    bdaySim(enable: boolean): void 

    webviewDetect(): DetectedWebView;

    getUnreadNotifications(): Promise<number>

    getPushService(): PushServiceInterface;


    getConfig(): PropertyConfig;

    on(eventName: string, callback: Action): any;
    one(eventName: string, callback: Action): any;
    off(eventName: string, callback: Action): any;



    _statsTrackPageview(page_title?: string, page_location?: string, other_params?: Record<string,any>): void;
    _statsTrackEvent(event_name: string, params?: Record<string,any>): void

}


export enum LinkTarget {
    SELF = "_self",
    BLANK = "_blank",
    PARENT = "_parent",
    TOP = "_top",
    UNFENCED_TOP = "_unfencedTop"
}

export enum MembershipDialogFlag {
    YEARLY_SUBSCRIPTION = "yearly_subscription",
    MONTHLY_SUBSCRIPTION = "monthly_subscription",
    SHOW_DISCOUNTS = "show_discounts",
    SHOW_FREETRIAL = "show_freetrial"
}


export const MembershipFlagsBasicConf = [MembershipDialogFlag.MONTHLY_SUBSCRIPTION, MembershipDialogFlag.YEARLY_SUBSCRIPTION];

export function isYearSub(flags: MembershipDialogFlag[]) {
    return flags && flags.length > 0 && flags.includes(MembershipDialogFlag.YEARLY_SUBSCRIPTION);
}

export function isFreeTrial(flags: MembershipDialogFlag[]) {
    return flags && flags.length > 0 && flags.includes(MembershipDialogFlag.SHOW_FREETRIAL);
}
export function useFreeTrial(flags: MembershipDialogFlag[], propertyConfig: PropertyConfig) {
    return propertyConfig.membership.enable && propertyConfig.membership?.freeTrialSupport && (isFreeTrial(flags) || propertyConfig.membership?.freeTrialDefaultEnabled);
}

export function isDiscount(flags: MembershipDialogFlag[]) {
    return flags && flags.length > 0 && flags.includes(MembershipDialogFlag.SHOW_DISCOUNTS);
}

export function isMonthSub(flags: MembershipDialogFlag[]) {
    return flags && flags.length > 0 && flags.includes(MembershipDialogFlag.MONTHLY_SUBSCRIPTION);
}


export function isMembershipRequired(flags: MembershipDialogFlag[]) {
    return isYearSub(flags) || isMonthSub(flags);
}

export interface BuyMembershipOptions {
    newTab?: boolean,
    returnUrl?: string,
    useDiscount?: boolean,
    useFreeTrial?: boolean,
    includeBook?: boolean
}

export enum MailFixDialogOutcome {
    LOGIN_COMPLETED_VIA_OTP = "login_completed_via_otp",
    VERIFICATION_COMPLETED_VIA_OTP = "verification_completed_via_otp",
    WAITING_LINK_CLICK = "waiting_link_click",
    USER_CLOSED_DIALOG = "user_closed_dialog"
}


export interface ClientSdkPublicInterface 
    extends ClientSdkCorePublic,
            ApplicationUrlHandler {


    errorSubject: MergedObservable<UserActionError>;
    successSubject: MergedObservable<any>;

    /**
     * marca nei cookie la data corrente come ultima
     * data in cui è stato mostrato il popup.
     * Il client che integra l'utilizzo della sdk
     * ha la responsabilità di chiamare questo metodo.
     */
    markPopupShowed(): void
    /**
    * Serve ad eseguire le azioni, qualora richieste,
    * da eseguire successivamente all'atto della chiusura
    * del popup. Il client che integra la sdk
    * ha la responsabilità di invocare questo metodo.
    *
    * Questo metodo ad esempio, esegue il refuse
    * per gli utenti snu.
    *
    */
    markPopupClosed(): void


  

    

    parseAndAttachFollows(c: CollectionTarget): Promise<void>
    parseAndAttachSliders(c: CollectionTarget): Promise<void>
    parseAndAttachNewsletterSubscription(c: CollectionTarget): Promise<void>
    /**
     * metodo duplicato con naming diverso per retrocompatibilità
     * @deprecated
     * usare `parseAndAttachBookmark`
     * @param c 
     */
    parseAndAttachSave(c: CollectionTarget): Promise<void>
    parseAndAttachBookmark(c: CollectionTarget): Promise<void>
     /**
     * effettua il parsing delle funzioni in pagina
     * metodo vecchio lasciato per retrocompatibilità
     * @deprecated
     * va utilizzato `parseFunctionalities`
     */
    parse(targets: CollectionTarget|CollectionTarget[], options: OldFormatParseOptions, triggeringEvent?: ActionableEvents): Promise<void>
    parseFunctionalities(opts: ParseOptionGroup): Promise<void>

    execCTA(): Promise<void>

    forceCTAAction(action: ManualAction): Promise<void>;

    getGui(): Promise<SdkGuiControllerInterface>
    
    supportsPushNotifications(): boolean;

    showLoginDialog(return_url?: string, options?: {title?: string,subtitle?: string,cta?: CTAStatsDetails}): Promise<SessionObject>;

    
    
    showMailFixOrLoginDialog(isFix: boolean, 
        sourceIntent: UserActionIdentifier,
        afterRegistrationCallback: (d: SubscriptionCallbackData) => void,
        mailRedirectUrl?: string,
        topic_label?: string,
        options?: {
            cta?: CTAStatsDetails
        }
    ): Promise<MailFixDialogOutcome>;


    externalMailFixOrLoginSubmit(askRes: EmailLoginAskResponse,
        userData: Ask1stPartyEmailAuthData, 
        sourceIntent: UserActionIdentifier,
        afterRegistrationCallback: (d: SubscriptionCallbackData) => void,
        options?: {
            cta?: CTAStatsDetails
        }): Promise<MailFixDialogOutcome>;

    

    showBuyDialog(return_url?: string, membershipFlags?: MembershipDialogFlag[], options?: {
            title?: string,
            subtitle?: string,
            buttonText?: string,
            cta?: CTAStatsDetails
        }): Promise<SessionObject | null>;
    showThankYouForRegistrationDialog(sourceIntent: UserActionIdentifier, email: string, youWereMailed: boolean, topicLabel?: string): Promise<void>;
    displayMessageDialog(title: string, subtitle?: string);

    isMember(): Promise<boolean>;
    handleBuyPersonalMembershipMonth(options?: BuyMembershipOptions);
    handleBuyPersonalMembershipYear(options?: BuyMembershipOptions);
    handleBuyGiftMembershipMonth(options?: BuyMembershipOptions);
    handleBuyGiftMembershipYear(options?: BuyMembershipOptions);
    handleMembershipSwitchToYearly(options?: BuyMembershipOptions);

    gotoUrlAfterLogin(url: string, target?: LinkTarget,
        options?: {dialog_title?: string, 
                   dialog_subtitle?: string,
                   utm_source?: string,
                   utm_medium?: string
                },
        membershipFlags?: MembershipDialogFlag[],
        extent?: LoginExtent,
        overrideBarrierDisplayMode?: BarrierDisplayMode
        ): Promise<void>;
    
}